import React, { ReactNode, useRef } from 'react';
import { Message_t, sendExecuteRequest, SocketState_t } from './Socket.tsx';
import { LoadingSpinner } from './Loading.tsx';
import { ReactComponent as ResetIcon } from './reset-icon.svg';
import { ReactComponent as LightbulbIcon } from './lightbulb-icon.svg';

const TIMEOUT_MS = 1000;
interface PlayButtonProps_t {
  onClick: () => void;
}

export type ClipboardButtonProps_t = {
  onClick: () => void;
};

const ClipboardButton: React.FC<ClipboardButtonProps_t> = ({ onClick }) => {
  return (
    <button className="clipboard-btn" onClick={onClick} title="Copy Code to Clipboard">
      <span className="clipboard-icon" />
    </button>
  );
};


const PlayButton: React.FC<PlayButtonProps_t> = ({ onClick }) => {
  return (
    <button className="play-btn" onClick={onClick} title="Compile & Run">
      <span className="play-icon" />
    </button>
  );
};

const ScrollBackButton: React.FC<PlayButtonProps_t> = ({ onClick }) => {
  return (
    <button className="arrow-btn" onClick={onClick} title="Scroll to Start of this Part">
      <span className="arrow-icon" />
    </button>
  );
};

const ResetButton: React.FC<PlayButtonProps_t> = ({ onClick }) => {
  return (
    <button className="reset-btn" onClick={onClick} title="Reset the Code in this Part">
      <ResetIcon className="reset-icon" />
    </button>
  );
}
const SolutionButton: React.FC<PlayButtonProps_t> = ({ onClick }) => {
  return (
    <button className="solution-btn" onClick={onClick} title='Use Solution for this Part'>
      <LightbulbIcon className="solution-icon" />
    </button>
  );
}

export type CodeSection_t = {
  name: string;
  solution: string;
  blank: string;
  refIndex: number;
}

interface EditorProps_t {
  codeSection: CodeSection_t;
  ref: React.RefObject<HTMLDivElement | null>;
  socket: WebSocket;
  socketState: SocketState_t;
  setSocketState: React.Dispatch<React.SetStateAction<SocketState_t>>;
  isTest: boolean;
  setDoc: (saved: string) => void;
  getDoc: () => string;
  viewIsReady: boolean;
  scrollOffset: number;
}

export const Editor: React.FC<EditorProps_t> = (props) => {
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loading, setLoading] = React.useState(false);
  const codeSection = props.codeSection;

  const codeIsEmpty = props.getDoc() === '';
  const name = codeSection.name;
  const viewIsReady = props.viewIsReady;
  const scrollOffset = props.scrollOffset;
  // This scrolls the Codemirror editor to the part of the code
  // that we are working on right now. For example it starts the 
  // editor at part 1.4 rather than the top of the file.
  // You will not believe how hard it was to get this to work.
  // Codemirror has a built in scrollIntoView but that scrolled the entire page 
  // and no combination of outer divs with overflow: auto etc. styling would prevent that.
  // Instead the approach I found was to find the cm-scroller element inside of the
  // Codemirror element and scroll that manually.
  const scrollToThisSection = React.useCallback(() => {
    const lines = props.getDoc().split('\n');

    const indexOfSectionStart = lines.findIndex(line => line === name);
    if (!viewIsReady) return;

    const editor = document.getElementById(name);

    if (editor) {
      // find all elements of class cm-scroller under the editor 
      const scrollableDivArr = editor.querySelectorAll(".cm-scroller");

      // there should only be one
      for (const div of scrollableDivArr) {
        if (div) {
          // TODO 
          // for some reason, later code blocks don't seem to scroll as much so I add an offset
          // i'm not totally sure why 
          const sectionStart = (div.scrollHeight - div.clientHeight) * (indexOfSectionStart / lines.length) + scrollOffset;

          div.scrollTop = sectionStart;
        }
      }
    }
  }, [codeIsEmpty, name, viewIsReady, scrollOffset]);

  React.useEffect(() => {
    scrollToThisSection();
  }, [codeIsEmpty, name, viewIsReady]);

  const seqNo = props.socketState.editorNameToSeqNumber[name];

  const messages = React.useMemo(() => {
    return seqNo != null ? props.socketState.messages.filter(m => {
      return m.meta.sequenceNumber === seqNo;
    }) : []
  }, [seqNo, props.socketState.messages]);

  const numMessages = messages.length;

  React.useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const haveEndMessage = messages.some(m => m.type === 'output/execute/wsExecuteEnd');
    if (haveEndMessage) {
      timerRef.current = null;
      setLoading(false);
    } else {
      timerRef.current = setTimeout(() => {
        setLoading(false);
        timerRef.current = null;
      }, TIMEOUT_MS);
    }

  }, [numMessages]);

  const spliceIn = React.useCallback((currentCode: string, toSpliceIn: string, name: string) => {
    let newCode = '';
    const lineIterator = currentCode.split("\n")[Symbol.iterator]();

    // find the start of this code block
    for (const line of lineIterator) {
      if (line === name) {
        break;
      } else {
        newCode += line + '\n';
      }
    }

    newCode += toSpliceIn;

    // skip the existing code for this code block
    for (const line of lineIterator) {
      if (line.startsWith("// ===")) {
        newCode += line + '\n';
        break;
      }
    }

    // add the code after the code block
    for (const line of lineIterator) {
      newCode += line + '\n';
    }

    return newCode;
  }, []);

  const onClick = () => {
    setLoading(true);
    timerRef.current = setTimeout(() => {
      setLoading(false);
      timerRef.current = null;
    }, TIMEOUT_MS);

    const sequenceNumber = props.socketState.sequenceNumber + 1;
    sendExecuteRequest(props.socket, sequenceNumber, props.getDoc(), props.isTest);
    props.setSocketState((prevState) => {
      const nextSequenceNumber = prevState.sequenceNumber + 1;
      return {
        ...prevState,
        sequenceNumber: nextSequenceNumber,
        editorNameToSeqNumber: {
          ...prevState.editorNameToSeqNumber,
          [name]: nextSequenceNumber,
        },
      };
    });
  }

  return (
    <div style={{ background: '#80808057', padding: '4px' }}>
      <div style={{
        resize: "vertical",   // <-- only vertical resize
        overflow: "auto",
        border: "1px solid #ccc",
        height: "500px",      // initial height
        minHeight: "100px",   // minimum height when resizing
      }}>
        {/* IMPORTANT id={name} lets us find the div to scroll */}
        <div style={{ height: '100%', width: '100%', cursor: 'text' }} ref={props.ref} id={name} />
      </div>
      <div className="button-row">
        <div className="left-buttons">
          <PlayButton onClick={onClick} />
          <ClipboardButton onClick={async () => {
            try {
              await navigator.clipboard.writeText(props.getDoc());
            } catch (err) {
              console.error('Failed to copy text: ', err);
            }
          }} />
          {loading && <LoadingSpinner />}
        </div>
        <div className='left-buttons'>
          <SolutionButton onClick={() => {
            let newDoc = spliceIn(props.getDoc(), props.codeSection.solution, props.codeSection.name);
            props.setDoc(newDoc);
            scrollToThisSection();
          }} />
          <ResetButton onClick={() => {
            let newDoc = spliceIn(props.getDoc(), props.codeSection.blank, props.codeSection.name);
            props.setDoc(newDoc);
            scrollToThisSection();
          }} />
          <ScrollBackButton onClick={scrollToThisSection} />
        </div>
      </div>
      <OutputBetter
        messages={messages}
      />
    </div>
  );
}

interface OutputProps_t {
  messages: Message_t[];
}

// Helpers to classify stderr
const classifyStderr = (text: string) => {
  const trimmed = text.trimStart();

  if (trimmed.startsWith("error[")) return "error";
  if (trimmed.startsWith("error")) return "error";
  if (trimmed.startsWith(":")) return "prev";

  if (trimmed.startsWith("warning[")) return "warning";
  if (trimmed.startsWith("warning")) return "warning";

  if (
    trimmed.startsWith("Compiling") ||
    trimmed.startsWith("Running") ||
    trimmed.includes("profile") ||
    trimmed.includes("Finished")
  ) {
    return "info";
  }

  return "other";
};

const OutputBetter: React.FC<OutputProps_t> = ({ messages }) => {
  const [out, setOut] = React.useState("");
  const [err, setErr] = React.useState<ReactNode>("");

  const outRef = useRef(null);
  const errRef = useRef(null);

  React.useEffect(() => {
    const el = outRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [out]);

  React.useEffect(() => {
    const el = errRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [err]);

  React.useEffect(() => {
    let stdOut = '';

    for (const m of messages) {
      if (m.type === 'output/execute/wsExecuteStdout') {
        stdOut += m.payload;
      }
    }

    let flattened: string[] = [];
    let cur = '';

    // we want to format the Stderr messages with colors for warnings and errors 
    // but sometimes a single line is split across multiple messages so we flatten 
    // them here
    for (const m of messages) {
      if (m.type === 'output/execute/wsExecuteStderr') {
        for (const c of m.payload) {
          if (c != '\n') {
            cur += c;
          } else {
            flattened.push(cur);
            cur = '';
          }
        }
      }
    }

    // push last line if it exists
    if (cur) {
      flattened.push(cur);
    }

    let stdErr = (<div>
      {flattened.map((line, i) => {
        let category = classifyStderr(line);
        return (
          <div key={i} className={`stderr-block ${category}`}>
            <pre className="stderr-text">{line}</pre>
          </div>
        );
      })}
    </div>);

    setOut(stdOut);
    setErr(stdErr);
  }, [messages, setOut, setErr]);

  return (
    <div className='output-wrapper'>
      <pre className='stdout'><h4>Compiler Output</h4></pre>
      <div className="output-container" ref={errRef}>
        {err}
      </div>
      <pre className='stdout'><h4>Stdout</h4></pre>
      <div className="output-container" ref={outRef}>
        <pre className="stdout">
          {out}
        </pre>
      </div>
    </div>
  )
}